import { HttpClient, HttpParams } from '@angular/common/http'
import { Injectable, inject } from '@angular/core'
import { ApiResponse, MaybeError } from '../model/api.model'
import { environment } from '../../../environments/environment'
import { catchAndHandleError, mapToMaybeData } from '../util'
import {
  ColumnRequest,
  CommentRequest,
  CommentResponse,
  TeamBulkRequest,
  TrackingData,
  TrackingDetailsData,
  TrackingDetailsResponseData,
  TrackingResponseData,
  TrackingStatusResponse,
  TrackingViewsData,
  TrackingViewsResponseData,
  UpdateColumnValueResponse,
} from 'src/app/tracking/tracking.model'
import { map, Observable } from 'rxjs'
import {
  convertMyViewTrackingsFromResponse,
  convertTrackingDetailsFromResponse,
  convertTrackingsFromResponse,
} from './tracking.convertor'
import { SelectedUserPreferences } from 'src/app/core/model/reinsurer.model'
import { toIsoDateString } from 'src/app/shared/util/date'

@Injectable({
  providedIn: 'root',
})
export class TrackingService {
  private httpClient = inject(HttpClient)

  getTracking(): ApiResponse<TrackingData[]> {
    const url = `${environment.internalApi.base}${environment.internalApi.placementTracking}`
    return this.httpClient
      .get<TrackingResponseData[]>(url)
      .pipe(
        map(convertTrackingsFromResponse),
        mapToMaybeData(),
        catchAndHandleError('Fetch Tracking Data'),
      )
  }

  getTrackingDetails(programId: number): ApiResponse<TrackingDetailsData[]> {
    const url = `${environment.internalApi.base}${environment.internalApi.placementTracking}/details/${programId}`
    return this.httpClient
      .get<TrackingDetailsResponseData[]>(url)
      .pipe(
        map(convertTrackingDetailsFromResponse),
        mapToMaybeData(),
        catchAndHandleError('Fetch Tracking Details Data'),
      )
  }

  getMyViewTracking(minDate?: string, maxDate?: string): ApiResponse<TrackingViewsData[]> {
    let params = new HttpParams()
    if (minDate) params = params.set('inceptionDateFrom', toIsoDateString(minDate))
    if (maxDate) params = params.set('inceptionDateTo', toIsoDateString(maxDate))
    const url = `${environment.internalApi.base}${environment.internalApi.placementTracking}/my-view`
    return this.httpClient
      .get<TrackingViewsResponseData[]>(url, { params })
      .pipe(
        map(convertMyViewTrackingsFromResponse),
        mapToMaybeData(),
        catchAndHandleError('Fetch My View Tracking Data'),
      )
  }

  getTeamViewTracking(minDate?: string, maxDate?: string): ApiResponse<TrackingViewsData[]> {
    let params = new HttpParams()
    if (minDate) params = params.set('inceptionDateFrom', toIsoDateString(minDate))
    if (maxDate) params = params.set('inceptionDateTo', toIsoDateString(maxDate))
    const url = `${environment.internalApi.base}${environment.internalApi.placementTracking}/team-view`
    return this.httpClient
      .get<TrackingViewsResponseData[]>(url, { params })
      .pipe(
        map(convertMyViewTrackingsFromResponse),
        mapToMaybeData(),
        catchAndHandleError('Fetch Team View Tracking Data'),
      )
  }

  getClientViewTracking(minDate?: string, maxDate?: string): ApiResponse<TrackingViewsData[]> {
    let params = new HttpParams()
    if (minDate) params = params.set('inceptionDateFrom', toIsoDateString(minDate))
    if (maxDate) params = params.set('inceptionDateTo', toIsoDateString(maxDate))
    const url = `${environment.internalApi.base}${environment.internalApi.placementTracking}/client-view`
    return this.httpClient
      .get<TrackingViewsResponseData[]>(url, { params })
      .pipe(
        map(convertMyViewTrackingsFromResponse),
        mapToMaybeData(),
        catchAndHandleError('Fetch Client View Tracking Data'),
      )
  }
  updateColumnValue(
    opportunityId: number,
    columnValue: ColumnRequest,
  ): Observable<TrackingStatusResponse> {
    const url = `${environment.internalApi.base}${environment.internalApi.placementTracking}/opportunity/${opportunityId}/status`
    return this.httpClient
      .post<TrackingStatusResponse>(url, columnValue)
      .pipe(catchAndHandleError('Post Column Value'))
  }

  updateUserValue(
    opportunityId: number,
    columnValue: TeamBulkRequest,
  ): Observable<UpdateColumnValueResponse> {
    const url = `${environment.internalApi.base}${environment.internalApi.placementTracking}/opportunity/${opportunityId}/bulk-team-member`
    return this.httpClient
      .post<UpdateColumnValueResponse>(url, columnValue)
      .pipe(catchAndHandleError('Post User Value'))
  }

  updateComment(
    opportunityId: number,
    columnValue: CommentRequest,
  ): Observable<UpdateColumnValueResponse> {
    const url = `${environment.internalApi.base}${environment.internalApi.placementTracking}/opportunity/${opportunityId}/comment`
    return this.httpClient
      .post<UpdateColumnValueResponse>(url, columnValue)
      .pipe(catchAndHandleError('Post comment Value'))
  }

  deleteUserPreference(preference: SelectedUserPreferences): Observable<SelectedUserPreferences> {
    const url = `${environment.internalApi.base}${environment.internalApi.userPreferences}/${preference.id}`
    return this.httpClient
      .delete<SelectedUserPreferences>(url)
      .pipe(catchAndHandleError('Delete User Preference'))
  }

  updateUserPreference(preference: SelectedUserPreferences): Observable<SelectedUserPreferences> {
    const url = `${environment.internalApi.base}${environment.internalApi.userPreferences}/tracking`
    return this.httpClient
      .put<SelectedUserPreferences>(url, preference)
      .pipe(catchAndHandleError('Post User Preference'))
  }
}
